import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Download, BookOpen, Layers, FileCode2 } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import { SITE, GLOSSARY, MATURITY_MODELS, MATURITY_SCALE, TOOLKITS, TEMPLATES } from "@/data/content";
import { MATURITY } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function Resources() {
  return (
    <>
      <PageHeader
        overline="Resources and Tools"
        title="Practical instruments for leaders."
        lead="Three maturity models, briefing toolkits, working templates, a plain-language glossary, and curated reading — turning insight into instruments leaders can use."
        image="/resources-hero.jpg"
      />

      {/* Multi-dimension Maturity model */}
      <MaturityModel />

      {/* Toolkits */}
      <section className="ccdr-section">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-5">
              <Layers className="w-8 h-8 mb-5" style={{ color: "var(--ccdr-primary)" }} />
              <p className="ccdr-eyebrow mb-5">Toolkits</p>
              <h2 className="ccdr-h2">Decision-ready packs.</h2>
            </div>
            <div className="lg:col-span-7 lg:pt-3">
              <p className="ccdr-lead">
                Short, audience-focused packs that gather the most relevant briefings, framework summaries, and checklists
                for a specific decision.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {TOOLKITS.map((t) => (
              <div key={t.name} className="bg-white p-7 flex flex-col">
                <h3 className="ccdr-h3 mb-2">{t.name}</h3>
                <p className="text-sm text-[#4B5563] mb-5 flex-1">{t.body}</p>
                <a
                  href={`mailto:${SITE.contactEmail}?subject=${encodeURIComponent("Toolkit request — " + t.name)}&body=${encodeURIComponent("Please send me the toolkit: " + t.name + "\n\nName:\nOrganisation:\nRegion (EU / AU / US / Other):\n")}`}
                  className="self-start inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B4F6C] hover:text-[#053043]"
                >
                  <Download className="w-3.5 h-3.5" /> Request
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates */}
      <section className="ccdr-section bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-5">
              <FileCode2 className="w-8 h-8 mb-5" style={{ color: "var(--ccdr-primary)" }} />
              <p className="ccdr-eyebrow mb-5">Templates</p>
              <h2 className="ccdr-h2">Working tools to take into the room.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {TEMPLATES.map((t) => (
              <div key={t.name} className="bg-white p-6">
                <h3 className="text-base mb-2" style={{ color: "var(--ccdr-primary-dark)", fontWeight: 500 }}>
                  {t.name}
                </h3>
                <p className="text-sm text-[#4B5563]">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Glossary */}
      <section className="ccdr-section">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <BookOpen className="w-8 h-8 mb-5" style={{ color: "var(--ccdr-primary)" }} />
            <p className="ccdr-eyebrow mb-5">Glossary</p>
            <h2 className="ccdr-h2 mb-6">Plain-language definitions.</h2>
            <p className="ccdr-prose">
              The Centre maintains plain-language definitions of the terms used across the site, so that a reader new to
              a domain is never lost.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB] max-h-[520px] overflow-y-auto">
              {GLOSSARY.map((g) => (
                <details key={g.term} className="p-5 hover:bg-[#FAFAFA]">
                  <summary
                    className="cursor-pointer text-sm"
                    style={{ color: "var(--ccdr-primary-dark)", fontWeight: 500 }}
                  >
                    {g.term}
                  </summary>
                  <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{g.body}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Start the maturity self-assessment, or download a briefing toolkit."
        primary={{ to: "/get-involved", label: "Talk to the Centre" }}
      />
    </>
  );
}

function MaturityModel() {
  const modelKeys = Object.keys(MATURITY_MODELS);
  const [activeModel, setActiveModel] = useState(modelKeys[1]); // default to AI assurance (largest)
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const model = MATURITY_MODELS[activeModel];
  const domains = model.domains;
  const allAnswered = useMemo(
    () => domains.every((d) => answers[d.key] !== undefined),
    [answers, domains]
  );

  const switchModel = (k) => {
    setActiveModel(k);
    setAnswers({});
    setResult(null);
  };

  const submit = async () => {
    if (!allAnswered) {
      toast.error(`Please answer all ${domains.length} domains before submitting.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([domain, score]) => ({ domain, score })),
      };
      const r = await axios.post(`${API}/maturity`, payload);
      setResult(r.data);
      toast.success("Assessment complete.");
    } catch (e) {
      toast.error("Could not complete the assessment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="ccdr-section bg-[#F3F4F6] border-y border-[#E5E7EB]">
      <div className="ccdr-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-10">
          <div className="lg:col-span-5">
            <p className="ccdr-eyebrow mb-5">Flagship tool</p>
            <h2 className="ccdr-h2 mb-6">Three maturity models, one self-assessment.</h2>
            <p className="ccdr-prose">
              Locate your institution on a path from initial awareness through to embedded, optimising practice. Choose
              the model that matches the conversation you need to lead — resilience governance, AI assurance, or
              standards alignment.
            </p>
          </div>

          <div className="lg:col-span-7">
            {/* Model selector */}
            <div className="grid grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mb-6">
              {modelKeys.map((k) => {
                const m = MATURITY_MODELS[k];
                const active = activeModel === k;
                return (
                  <button
                    key={k}
                    onClick={() => switchModel(k)}
                    data-testid={`maturity-model-${k}`}
                    className={`p-4 text-left transition-colors ${
                      active ? "bg-[#0B4F6C] text-white" : "bg-white text-[#111827] hover:bg-[#FAFAFA]"
                    }`}
                  >
                    <div className="text-xs uppercase tracking-[0.18em] mb-1 opacity-80">{`0${modelKeys.indexOf(k) + 1}`}</div>
                    <div className="text-sm font-medium leading-tight">{m.label}</div>
                  </button>
                );
              })}
            </div>

            <div className="bg-white border border-[#E5E7EB] p-8 lg:p-10">
              <p className="text-sm text-[#4B5563] mb-7 italic">{model.description}</p>
              <div className="space-y-8">
                {domains.map((d, idx) => (
                  <div key={d.key} data-testid={MATURITY.question(idx)}>
                    <div className="flex items-start gap-4 mb-4">
                      <span
                        className="text-xl tabular-nums flex-shrink-0"
                        style={{ color: "var(--ccdr-accent-dark)", fontWeight: 300 }}
                      >
                        {`0${idx + 1}`}
                      </span>
                      <div>
                        <h4
                          className="text-base mb-1"
                          style={{ color: "var(--ccdr-primary-dark)", fontWeight: 500 }}
                        >
                          {d.label}
                        </h4>
                        <p className="text-sm text-[#4B5563]">{d.prompt}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-2 pl-10">
                      {MATURITY_SCALE.map((s) => {
                        const active = answers[d.key] === s.value;
                        return (
                          <button
                            key={s.value}
                            data-testid={MATURITY.option(idx, s.value)}
                            onClick={() => setAnswers({ ...answers, [d.key]: s.value })}
                            className={`px-2 py-3 text-xs border transition-colors ${
                              active
                                ? "bg-[#0B4F6C] text-white border-[#0B4F6C]"
                                : "bg-white text-[#111827] border-[#E5E7EB] hover:border-[#0B4F6C]"
                            }`}
                            title={s.desc}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={submit}
                disabled={!allAnswered || submitting}
                data-testid={MATURITY.submit}
                className="ccdr-btn-primary mt-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Scoring…" : "See my maturity"} <ArrowRight className="w-4 h-4" />
              </button>

              {result && (
                <div data-testid={MATURITY.result} className="mt-10 ccdr-callout">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: "var(--ccdr-accent-dark)" }} />
                    <div>
                      <p
                        className="text-xs uppercase tracking-[0.22em] mb-2"
                        style={{ color: "var(--ccdr-accent-dark)" }}
                      >
                        {model.label} · Overall maturity
                      </p>
                      <h3 className="ccdr-h3 mb-3">
                        {result.overall_level} — {result.overall_score}/4
                      </h3>
                      <div className="text-sm text-[#053043] space-y-1.5">
                        {Object.entries(result.domain_levels).map(([k, v]) => {
                          const dom = domains.find((d) => d.key === k);
                          return (
                            <p key={k}>
                              <strong>{dom?.label || k}:</strong> {v}
                            </p>
                          );
                        })}
                      </div>
                      {result.recommendations?.length > 0 && (
                        <div className="mt-5">
                          <p
                            className="text-xs uppercase tracking-[0.22em] mb-2"
                            style={{ color: "var(--ccdr-accent-dark)" }}
                          >
                            Recommended next steps
                          </p>
                          <ul className="space-y-2 text-sm text-[#053043]">
                            {result.recommendations.map((r, i) => (
                              <li key={i}>• {r}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
